import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import {
  COVER_DOC_LIMITS,
  COVER_LAYOUTS,
  coverLayoutTexts,
  type CoverDoc,
  type CoverLayout,
  type CoverTextBox,
} from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { CoverThumb } from '../canvas/CoverThumb';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages, layoutMessages } from '../coverEditorMessages';
import { newCoverItemId } from '../doc/coverIds';
import { themeTextStyle } from '../doc/textTheme';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const LAYOUT_THUMB_PX = 128;

/**
 * The Text panel (design cover-editor d2): a new text box in the style of the release theme, and
 * the four layouts that place the title and the artist with one click. A layout keeps the free
 * boxes of the user.
 */
const TextPanelComponent: React.FC = () => {
  const intl = useIntl();
  const { state, dispatch, data, readOnly } = useCoverEditor();
  const { doc } = state;
  const { song } = data;

  const textsOf = useCallback(
    (layout: CoverLayout): CoverTextBox[] =>
      coverLayoutTexts({
        layout,
        themeId: doc.themeId,
        palette: doc.palette,
        title: song.title,
        artist: song.artistName,
      }),
    [doc.palette, doc.themeId, song.artistName, song.title]
  );
  const previews = useMemo(
    () =>
      COVER_LAYOUTS.map((layout) => ({
        layout,
        doc: { ...doc, texts: textsOf(layout), effects: [] } satisfies CoverDoc,
      })),
    [doc, textsOf]
  );
  const add = useCallback(() => {
    const style = themeTextStyle('free', doc.themeId, doc.palette);
    if (style === null) {
      return;
    }
    dispatch({
      type: 'addText',
      box: {
        ...style,
        id: newCoverItemId('t'),
        role: 'free',
        text: intl.formatMessage(coverEditorMessages.textNewBox),
        sizeShare: 0.05,
        align: 'center',
        x: 0.5,
        y: 0.5,
        width: 0.7,
        rotationDeg: 0,
      },
    });
  }, [dispatch, doc.palette, doc.themeId, intl]);
  const isFull = doc.texts.length >= COVER_DOC_LIMITS.texts;

  return (
    <>
      <h3 className={styles.paneTitle}>{intl.formatMessage(coverEditorMessages.toolText)}</h3>
      <SMLButton stretch omitCap onClick={add} disabled={readOnly || isFull}>
        {intl.formatMessage(coverEditorMessages.textAdd)}
      </SMLButton>
      {isFull && (
        <span className={styles.hint}>
          {intl.formatMessage(coverEditorMessages.textLimit, { count: COVER_DOC_LIMITS.texts })}
        </span>
      )}
      <span className={styles.eyebrow}>{intl.formatMessage(coverEditorMessages.textLayouts)}</span>
      <div className={styles.grid2}>
        {previews.map((preview) => (
          <button
            key={preview.layout}
            type="button"
            className={clsx(styles.preset)}
            disabled={readOnly}
            onClick={() => dispatch({ type: 'applyLayout', texts: textsOf(preview.layout) })}
          >
            <CoverThumb doc={preview.doc} size={LAYOUT_THUMB_PX} className={styles.presetThumb} />
            {intl.formatMessage(layoutMessages[preview.layout])}
          </button>
        ))}
      </div>
      <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.textLayoutsHint)}</span>
    </>
  );
};

export const TextPanel = TypedMemo(TextPanelComponent);
